import {
  DeesElement,
  customElement,
  property,
  state,
  html,
  css,
} from '@design.estate/dees-element';
import type { IKanbanComment, IKanbanProvider } from '../interfaces.js';
import { kanbanStyles } from '../kanban.styles.js';
import { kanbanError, kanbanEvent, kanbanInitials } from '../kanban.helpers.js';
import {
  referenceText,
  type TReferenceContent,
  type TReferenceSearch,
} from '../../00group-input/dees-input-reference/index.js';
import '../../00group-button/dees-button/dees-button.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-kanban-discussion': DeesKanbanDiscussion;
  }
}

@customElement('dees-kanban-discussion')
export class DeesKanbanDiscussion extends DeesElement {
  @property({ type: String })
  accessor ticketId = '';

  @property({ attribute: false })
  accessor comments: IKanbanComment[] = [];

  @property({ attribute: false })
  accessor draft: TReferenceContent = [];

  @property({ attribute: false })
  accessor searchTickets: TReferenceSearch | undefined;

  @property({ attribute: false })
  accessor addComment: IKanbanProvider['addComment'];

  @property({ type: Boolean })
  accessor loading = false;

  @property({ type: Boolean })
  accessor readOnly = false;

  @property({ type: String })
  accessor error = '';

  @state()
  private accessor pending = new Set<string>();

  @state()
  private accessor failures = new Map<string, string>();

  public static styles = [
    ...kanbanStyles,
    css`
      header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
      }

      h3 {
        font-size: var(--dees-font-footnote-size);
        font-weight: 600;
      }

      .refresh::part(button) {
        width: 28px;
        height: 28px;
        min-height: 28px;
      }

      ol {
        list-style: none;
        padding: 0;
        margin: 0;
      }

      li {
        display: grid;
        grid-template-columns: 28px minmax(0, 1fr);
        gap: 10px;
        padding: 10px 0;
      }

      li > .avatar {
        width: 28px;
        height: 28px;
        margin-top: 1px;
      }

      .author {
        font-weight: 600;
        font-size: var(--dees-font-footnote-size);
      }

      .meta {
        gap: 4px 10px;
      }

      time {
        font-size: var(--dees-font-caption1-size);
        color: var(--dees-color-text-secondary);
      }

      .message {
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        font-size: var(--dees-font-subhead-size);
        line-height: 22px;
        margin-top: 2px;
      }

      .reference {
        display: inline;
        border: 0;
        border-radius: var(--dees-radius-sm);
        padding: 1px 5px;
        color: var(--dees-color-link);
        background: var(--dees-color-fill-tertiary);
        font: inherit;
        font-weight: 500;
        cursor: pointer;
        text-align: left;
        overflow-wrap: anywhere;
      }

      .compose {
        margin: 14px 0 0 38px;
        padding: 0;
        border: 1px solid var(--dees-color-control-border);
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-control-bg);
      }

      .compose:has(dees-input-reference:focus-within) {
        outline: 2px solid var(--dees-color-focus-ring);
        outline-offset: 1px;
      }

      dees-input-reference {
        --dees-reference-min-height: 64px;
        --dees-reference-font-size: var(--dees-font-subhead-size);
      }

      dees-input-reference::part(editor) {
        border: 0;
        background: transparent;
        outline: none;
      }

      .compose .row {
        padding: 4px 10px 10px;
        gap: 8px;
      }

      .compose .caption {
        font-size: var(--dees-font-caption1-size);
      }

      .shortcut {
        white-space: nowrap;
      }

      .compose .error {
        padding: 0 12px 10px;
      }

      @media (max-width: 600px) {
        .compose {
          margin-left: 0;
        }
        .compose .caption {
          flex-basis: 100%;
        }
      }
    `,
  ];

  public render() {
    const pending = this.pending.has(this.ticketId);
    const comments = this.comments
      .filter(comment => comment.ticketId === this.ticketId)
      .slice()
      .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
    return html`<header>
      <h3>Discussion</h3>
      <span class="count">${comments.length}</span>
      <span class="spacer"></span>
      <dees-button
        class="refresh"
        size="icon"
        type="ghost"
        icon="lucide:RefreshCw"
        text="Refresh"
        .disabled=${this.loading}
        @clicked=${() => kanbanEvent(this, 'kanban-comments-refresh', { ticketId: this.ticketId })}
      ></dees-button>
    </header>
      ${this.loading ? html`<p class="caption" role="status">Loading comments…</p>` : ''}
      ${this.error ? html`<p class="error" role="alert">${this.error}</p>` : ''}
      <ol aria-label="Comments">${comments.map(comment => html`<li>
        <span class="avatar" aria-hidden="true">${kanbanInitials(comment.author.name)}</span><div>
        <div class="row meta">
          <span class="author">${comment.author.name}</span>
          <time datetime=${comment.createdAt} title=${comment.createdAt}
            >${new Date(comment.createdAt).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}</time>
        </div>
        <div class="message">${comment.body.map(part =>
          part.type === 'text'
            ? part.text
            : html`<button
                class="reference"
                @click=${() => kanbanEvent(this, 'kanban-ticket-open', { ticketId: part.id })}
              >${part.label}</button>`,
        )}</div>
      </div></li>`)}</ol>
      ${!comments.length && !this.loading && !this.error
        ? html`<p class="empty">Start the conversation.</p>`
        : ''}
      ${this.addComment && !this.readOnly
        ? html`<div class="compose">
            <dees-input-reference
              label="Comment"
              placeholder="Write a comment…"
              .documentKey=${this.ticketId}
              .value=${this.draft}
              .disabled=${pending}
              .searchReferences=${this.searchTickets}
              @reference-input=${(event: CustomEvent<{ value: TReferenceContent }>) => {
                this.draft = event.detail.value;
                kanbanEvent(this, 'kanban-comment-draft', {
                  ticketId: this.ticketId,
                  body: this.draft,
                });
              }}
              @reference-open=${(event: CustomEvent<{ id: string }>) =>
                kanbanEvent(this, 'kanban-ticket-open', { ticketId: event.detail.id })}
              @reference-submit=${() => {
                void this.submit();
              }}
            ></dees-input-reference>
            <div class="row">
              <span class="caption">Type <strong>#_</strong> to link a ticket · <span class="shortcut">Ctrl/⌘ Enter to send</span></span>
              <span class="spacer"></span>
              <dees-button
                type="accent"
                size="sm"
                text="Comment"
                .disabled=${pending || !referenceText(this.draft).trim()}
                .status=${pending ? 'pending' : 'normal'}
                @clicked=${() => {
                  void this.submit();
                }}
              ></dees-button>
            </div>
            ${this.failures.get(this.ticketId)
              ? html`<p class="error" role="alert">${this.failures.get(this.ticketId)}</p>`
              : ''}
          </div>`
        : html`<p class="caption">Comments are read-only.</p>`}`;
  }

  public async submit(): Promise<void> {
    const ticketId = this.ticketId;
    if (
      !ticketId ||
      !this.addComment ||
      this.readOnly ||
      this.pending.has(ticketId) ||
      !referenceText(this.draft).trim()
    )
      return;
    const body = this.draft.map(part => ({ ...part }));
    this.pending = new Set(this.pending).add(ticketId);
    this.failures = new Map(this.failures);
    this.failures.delete(ticketId);
    try {
      const comment = await this.addComment({ ticketId, body });
      if (comment.ticketId !== ticketId)
        throw new Error('The saved comment belongs to a different ticket.');
      if (this.ticketId === ticketId && JSON.stringify(this.draft) === JSON.stringify(body))
        this.draft = [];
      kanbanEvent(this, 'kanban-comment-added', { comment, submittedBody: body });
    } catch (error) {
      this.failures = new Map(this.failures).set(ticketId, kanbanError(error));
    } finally {
      this.pending = new Set(this.pending);
      this.pending.delete(ticketId);
    }
  }
}
